import {CheckCircleIcon} from '@heroicons/react/24/solid';

import classNames from 'classnames';
import {Field, FormikProvider,useFormik} from 'formik';
import * as React from 'react';
import {useState} from 'react';
import * as Yup from 'yup';

import {supabase} from '_utils';

const validationSchema = Yup.object().shape({
  propertyType: Yup.string().required('This information is required'),
  propertyStatus: Yup.string().required('This information is required'),
  budget: Yup.string().required('This information is required'),
  loanRequired: Yup.string().required('This information is required'),
  userName: Yup.string().required('This information is required'),
  email: Yup.string().required('This information is required'),
  phoneNumber: Yup.string().required('This information is required'),
});

interface FormValues {
  propertyType: string;
  propertyStatus: string;
  budget: string;
  loanRequired: string;
  areaSize?: number;
  userName: string;
  email: string;
  phoneNumber: string;
  note?: string;
}

interface Props {
  includeLink?: boolean;
}

const EnquiryForm = ({includeLink = false}: Props) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const initialValues: FormValues = {
    propertyType: '',
    propertyStatus: '',
    budget: '',
    loanRequired: '',
    areaSize: undefined,
    userName: '',
    email: '',
    phoneNumber: '',
    note: '',
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async values => {
      try {
        setIsSubmitting(true);
        await supabase.functions.invoke('submit-form', {
          body: {
            data: {
              ...values,
              projectLink: includeLink ? window.location.href : '',
            },
            formType: 'ENQUIRY',
          },
        });
        setSubmitSuccess(true);
        formik.resetForm();
      } catch (error) {
        console.log(error);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  return (
    <FormikProvider value={formik}>
      <form onSubmit={formik.handleSubmit} className="bg-[#f6f6f6] mb-10">
        <div className="relative">
          <div className="font-semibold text-gray1">
            What's your property type? <span className="text-red-500">*</span>
          </div>
          <div role="group">
            {['HDB', 'Condo', 'Landed', 'Commercial'].map(
              (propertyType, index) => (
                <label key={index} className="flex items-center mt-2.5">
                  <Field
                    type="radio"
                    name="propertyType"
                    value={propertyType}
                  />
                  <span className="ml-2.5">{propertyType}</span>
                </label>
              ),
            )}
          </div>
          <div className="text-red-500 text-sm absolute -bottom-4.5 left-0">
            {formik.touched.propertyType && formik.errors.propertyType}
          </div>
        </div>

        <div className="mt-10 relative">
          <div className="font-semibold text-gray1">
            What is your property status?{' '}
            <span className="text-red-500">*</span>
          </div>
          <div role="group">
            {['New', 'Resale', 'Existing'].map((propertyStatus, index) => (
              <label key={index} className="flex items-center mt-2.5">
                <Field
                  type="radio"
                  name="propertyStatus"
                  value={propertyStatus}
                />
                <span className="ml-2.5">{propertyStatus}</span>
              </label>
            ))}
          </div>
          <div className="text-red-500 text-sm absolute -bottom-4.5 left-0">
            {formik.touched.propertyStatus && formik.errors.propertyStatus}
          </div>
        </div>

        <div className="mt-10 relative">
          <div className="font-semibold text-gray1">
            What's your reno budget? <span className="text-red-500">*</span>
          </div>
          <div role="group">
            {['$10k - $25k', '$25K - $50K', '$50K - $100K', 'Over $100K'].map(
              (budget, index) => (
                <label key={index} className="flex items-center mt-2.5">
                  <Field type="radio" name="budget" value={budget} />
                  <span className="ml-2.5">{budget}</span>
                </label>
              ),
            )}
          </div>
          <div className="text-red-500 text-sm absolute -bottom-4.5 left-0">
            {formik.touched.budget && formik.errors.budget}
          </div>
        </div>

        <div className="mt-10 relative">
          <div className="font-semibold text-gray1">
            Is a loan required? <span className="text-red-500">*</span>
          </div>
          <div role="group">
            {['Yes', 'No'].map((loanRequired, index) => (
              <label key={index} className="flex items-center mt-2.5">
                <Field type="radio" name="loanRequired" value={loanRequired} />
                <span className="ml-2.5">{loanRequired}</span>
              </label>
            ))}
          </div>
          <div className="text-red-500 text-sm absolute -bottom-4.5 left-0">
            {formik.touched.loanRequired && formik.errors.loanRequired}
          </div>
        </div>

        <div className="mt-10 relative">
          <label htmlFor="areaSize" className="block font-semibold text-gray1">
            Area Size
          </label>
          <div className="relative mt-2 rounded-md">
            <input
              className="block w-full rounded-md py-2.5 border pl-4 pr-12 text-gray-900 focus:outline-none text-lg bg-input border-transparent"
              id="areaSize"
              name="areaSize"
              type="string"
              onChange={formik.handleChange}
              value={formik.values.areaSize}
            />
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <span className="text-gray-500 text-lg">㎡</span>
            </div>
          </div>
        </div>

        <div className="mt-10 relative">
          <label htmlFor="userName" className="block font-semibold text-gray1">
            Your Name <span className="text-red-500">*</span>
          </label>
          <input
            className={classNames(
              'mt-2 block w-full rounded-md py-2.5 border pl-4 pr-12 text-gray-900 focus:outline-none text-lg bg-input',
              formik.touched.userName && formik.errors.userName
                ? 'border-red-500'
                : 'border-transparent',
            )}
            id="userName"
            name="userName"
            onChange={formik.handleChange}
            value={formik.values.userName}
          />
          <div className="text-red-500 text-sm absolute -bottom-6 left-0">
            {formik.touched.userName && formik.errors.userName}
          </div>
        </div>

        <div className="mt-10 relative">
          <label
            htmlFor="email"
            className="block font-semibold text-gray1">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            className={classNames(
              'mt-2 block w-full rounded-md py-2.5 border pl-4 pr-12 text-gray-900 focus:outline-none text-lg bg-input',
              formik.touched.email && formik.errors.email
                ? 'border-red-500'
                : 'border-transparent',
            )}
            id="email"
            name="email"
            type="email"
            onChange={formik.handleChange}
            value={formik.values.email}
          />
          <div className="text-red-500 text-sm absolute -bottom-6 left-0 text-gray1">
            {formik.touched.email && formik.errors.email}
          </div>
        </div>

        <div className="mt-10 relative">
          <label
            htmlFor="phoneNumber"
            className="block font-semibold text-gray1">
            Contact Number <span className="text-red-500">*</span>
          </label>
          <input
            className={classNames(
              'mt-2 block w-full rounded-md py-2.5 border pl-4 pr-12 text-gray-900 focus:outline-none text-lg bg-input',
              formik.touched.phoneNumber && formik.errors.phoneNumber
                ? 'border-red-500'
                : 'border-transparent',
            )}
            id="phoneNumber"
            name="phoneNumber"
            onChange={formik.handleChange}
            value={formik.values.phoneNumber}
          />
          <div className="text-red-500 text-sm absolute -bottom-6 left-0">
            {formik.touched.phoneNumber && formik.errors.phoneNumber}
          </div>
        </div>

        <div className="mt-10 relative">
          <label
            htmlFor="note"
            className="block font-semibold text-gray1">
            Note
          </label>
          <textarea
            className={classNames(
              'mt-2 block w-full rounded-md py-2.5 border pl-4 pr-12 text-gray-900 focus:outline-none text-lg bg-input resize-none ',
              formik.touched.phoneNumber && formik.errors.phoneNumber
                ? 'border-red-500'
                : 'border-transparent',
            )}
            rows={6}
            id="note"
            name="note"
            onChange={formik.handleChange}
            value={formik.values.note}
          />
          <div className="text-red-500 text-sm absolute -bottom-6 left-0">
            {formik.touched.note && formik.errors.note}
          </div>
        </div>

        <div className="px-4">
          <button
            className={classNames(
              'w-full bg-primary py-3 font-semibold rounded-full text-white mt-12 hover:text-dark-blue',
              isSubmitting ? 'opacity-50' : '',
            )}
            type="submit">
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </button>
          {submitSuccess ? (
            <div>
              <div className="flex gap-x-2 items-center mt-4 justify-center">
                <CheckCircleIcon className="w-8 h-8 text-green-500" />
                <div className="text-lg font-semibold">
                  Successfully submitted!
                </div>
              </div>
              <div>
                <div className="mt-2 text-center">
                  Thanks for your inquiry. Our Team will contact you soon!
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </form>
    </FormikProvider>
  );
};

export default EnquiryForm;
